import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  ConfirmDialog,
  ModuleHeader
} from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, SyutSearchGrid, MinimizedModalStrip } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useApplications, useDeleteApplication } from 'hooks/api';
import type { ApplicationResponse } from 'api/types';
import ApplicationFormModal from '../../../modals/modules/platform-admin/ApplicationFormModal';
import ApplicationViewModal from '../../../modals/modules/platform-admin/ApplicationViewModal';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type ViewMode = 'list' | 'grid' | 'calendar';
type AppGridRow = ApplicationResponse & { id: string };

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  { id: 'select', label: '', field: 'select', required: true, defaultVisible: true, width: 50, sortable: false },
  { id: 'appCode', label: 'Code', field: 'appCode', required: false, defaultVisible: true, sortable: true },
  { id: 'appName', label: 'Name', field: 'appName', required: false, defaultVisible: true, sortable: true },
  { id: 'description', label: 'Description', field: 'description', required: false, defaultVisible: true, sortable: true },
  { id: 'appVersion', label: 'Version', field: 'appVersion', required: false, defaultVisible: true, sortable: true },
  { id: 'statusName', label: 'Status', field: 'statusName', required: false, defaultVisible: true, sortable: true },
  { id: 'createdAt', label: 'Created', field: 'createdAt', required: false, defaultVisible: true, sortable: true },
  { id: 'updatedAt', label: 'Updated', field: 'updatedAt', required: false, defaultVisible: false, sortable: true },
  { id: 'actions', label: 'Actions', field: 'actions', required: true, defaultVisible: true, width: 80, sortable: false },
];

// ============================================================================
// COLUMN SELECTOR COMPONENT
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [isMobile, setIsMobile] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev =>
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns.filter(col => !col.required).map(col => col.id);
    const requiredIds = columns.filter(col => col.required).map(col => col.id);
    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  const handleApply = () => { onApply(selectedColumns); setIsOpen(false); };
  const handleCancel = () => { setSelectedColumns(initialVisible); setIsOpen(false); };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <>
          {isMobile && (
            <div
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', zIndex: 999 }}
              onClick={() => setIsOpen(false)}
            />
          )}
          <div style={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? '10%' : 'calc(100% + 0.5rem)',
            left: isMobile ? '50%' : 'auto',
            right: isMobile ? 'auto' : 0,
            transform: isMobile ? 'translateX(-50%)' : 'none',
            width: isMobile ? 'calc(100vw - 2rem)' : '320px',
            maxWidth: '320px',
            maxHeight: isMobile ? 'calc(90vh - 2rem)' : '500px',
            background: 'var(--bg-primary)',
            border: '2px solid var(--primary)',
            borderRadius: '0.75rem',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            animation: 'slideDown 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '1rem 1.25rem',
              borderBottom: '2px solid var(--border-color)',
              background: 'var(--bg-secondary)',
              borderRadius: '0.75rem 0.75rem 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0
            }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                Manage Columns
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                style={{ width: '1.5rem', height: '1.5rem', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem' }}
              >
                <IconSystem.x size={16} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0', minHeight: 0 }}>
              {columns.map(column => (
                <div
                  key={column.id}
                  onClick={() => toggleColumn(column.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.625rem 1.25rem',
                    cursor: column.required ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    gap: '0.75rem',
                    opacity: column.required ? 0.6 : 1
                  }}
                >
                  <div style={{
                    width: '1.125rem',
                    height: '1.125rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--border-color)',
                    borderRadius: '0.25rem',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    ...(selectedColumns.includes(column.id) ? {
                      background: 'var(--accent-gradient)',
                      borderColor: 'var(--primary)',
                      color: 'white'
                    } : {})
                  }}>
                    {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                  </div>
                  <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>
                    {column.label}{column.required && ' *'}
                  </span>
                </div>
              ))}
            </div>

            <div style={{
              padding: '1rem',
              borderTop: '2px solid var(--border-color)',
              background: 'var(--bg-primary)',
              boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleSelectAll} style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                  {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
                </button>
                <button onClick={() => setConfirmResetOpen(true)} style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                  Reset Default
                </button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={handleCancel} className="btn btn-secondary">Cancel</button>
                <button onClick={handleApply} className="btn btn-primary">Apply</button>
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

// ============================================================================
// APPLICATION CARD COMPONENT (Grid View)
// ============================================================================

interface AppCardProps {
  app: AppGridRow;
  onView: (app: AppGridRow) => void;
}

const AppCard: React.FC<AppCardProps> = ({ app, onView }) => {
  const menuItems: ActionMenuItem[] = useMemo(() => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => onView(app) },
  ], [app, onView]);

  return (
    <div style={{
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      borderRadius: '0.75rem',
      padding: '1.25rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.2s ease',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            {app.appCode}
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.5rem' }}>
            {app.appName}
          </h3>
        </div>
        <ActionMenu items={menuItems} />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--border-color)'
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Version</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{app.appVersion || '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Status</div>
          {(() => {
            const code = (app.statusCode || '').toLowerCase();
            const isActive = code.includes('active');
            const label = isActive ? 'Active' : (app.statusName || '').replace(/\s*application\s*/i, '') || '-';
            return <span className={`badge ${isActive ? 'badge-active' : 'badge-inactive'}`}>{label}</span>;
          })()}
        </div>
      </div>

      {app.createdAt && (
        <div style={{ paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          <strong>Created:</strong> {new Date(app.createdAt).toLocaleDateString()}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN APPLICATION MASTER SCREEN COMPONENT
// ============================================================================

interface ApplicationMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const ApplicationMasterScreen: React.FC<ApplicationMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, getMinimizedModal, dismiss } = useModalMinimize();
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('');

  // ---- API state ----
  const { data: applications, loading, refetch } = useApplications();
  const { deleteApplication, loading: deleting } = useDeleteApplication();

  // Map applications to include `id` field required by SyutGrid
  const gridData: AppGridRow[] = useMemo(
    () => applications.map(a => ({ ...a, id: a.appId })),
    [applications],
  );

  // ---- Column visibility ----
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const defaultVisible = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    return defaultVisible;
  });

  // ---- UI state ----
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [gridPage, setGridPage] = useState(0);
  const [gridPageSize, setGridPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [versionFilter, setVersionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createdFromFilter, setCreatedFromFilter] = useState('');
  const [createdToFilter, setCreatedToFilter] = useState('');
  const [, setSelectedRows] = useState<AppGridRow[]>([]);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);

  // ---- Modal state ----
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState<ApplicationResponse | null>(null);

  // ---- Load saved column preferences ----
  useEffect(() => {
    const saved = localStorage.getItem('application-master-columns');
    if (saved) {
      try { setVisibleColumns(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  // ---- F2 keyboard shortcut for search popup ----
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !formModalOpen && !viewModalOpen) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, formModalOpen, viewModalOpen]);

  // ---- Filtered data ----
  const filteredData: AppGridRow[] = useMemo(() => {
    let result = gridData;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(a =>
        a.appName.toLowerCase().includes(q) ||
        a.appCode.toLowerCase().includes(q) ||
        (a.appVersion || '').toLowerCase().includes(q)
      );
    }
    if (codeFilter.trim()) {
      const q = codeFilter.toLowerCase();
      result = result.filter(a => a.appCode.toLowerCase().includes(q));
    }
    if (nameFilter.trim()) {
      const q = nameFilter.toLowerCase();
      result = result.filter(a => a.appName.toLowerCase().includes(q));
    }
    if (versionFilter.trim()) {
      const q = versionFilter.toLowerCase();
      result = result.filter(a => (a.appVersion || '').toLowerCase().includes(q));
    }
    if (statusFilter) {
      result = result.filter(a => (a.statusCode || '').toLowerCase() === statusFilter.toLowerCase());
    }
    if (createdFromFilter) {
      result = result.filter(a => a.createdAt && a.createdAt >= createdFromFilter);
    }
    if (createdToFilter) {
      result = result.filter(a => a.createdAt && a.createdAt <= createdToFilter);
    }
    return result;
  }, [gridData, searchQuery, codeFilter, nameFilter, versionFilter, statusFilter, createdFromFilter, createdToFilter]);

  // ---- Column visibility handlers ----
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('application-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('application-master-columns');
  }, []);

  // ---- Handlers ----
  const handleView = useCallback((app: ApplicationResponse) => {
    const viewId = tabId ? `${tabId}-view-modal-${app.appCode}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismiss(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedApp(app);
    setViewModalOpen(true);
  }, [tabId, isMinimized, getMinimizedModal, dismiss]);

  const handleRestoreView = useCallback((appCode: string) => {
    const app = applications.find(a => a.appCode === appCode);
    if (app) {
      setSelectedApp(app);
      setViewModalOpen(true);
    }
  }, [applications]);

  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedApp(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback((app: ApplicationResponse) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${app.appCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismiss(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedApp(app);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, getMinimizedModal, dismiss]);

  const handleDeleteClick = useCallback((app: ApplicationResponse) => {
    setSelectedApp(app);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedApp) return;
    try {
      await deleteApplication(selectedApp.appId, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedApp(null);
          notificationService.toast.success(`"${selectedApp.appName}" has been deleted successfully.`);
          refetch();
        },
      });
    } catch {
      notificationService.toast.error('Failed to delete application. Please try again.');
    }
  }, [selectedApp, deleteApplication, refetch]);

  const handleFormSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedApp(null);
    refetch();
  }, [refetch]);

  // ---- Grid columns ----
  const tableColumns: SyutGridColumn<AppGridRow>[] = useMemo(() => [
    { id: 'appCode', header: 'Code', accessorKey: 'appCode', size: 140 },
    { id: 'appName', header: 'Name', accessorKey: 'appName', size: 250 },
    { id: 'description', header: 'Description', accessorKey: 'description',
      cell: ({ getValue }) => (getValue() as string) || '-',
    },
    { id: 'appVersion', header: 'Version', accessorKey: 'appVersion', size: 120,
      cell: ({ getValue }) => (getValue() as string) || '-',
    },
    {
      id: 'statusName',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 120,
      cell: ({ row }) => {
        const code = (row.original.statusCode || '').toLowerCase();
        const isActive = code.includes('active');
        const label = isActive ? 'Active' : (row.original.statusName || 'Unknown').replace(/\s*application\s*/i, '');
        return <span className={`badge ${isActive ? 'badge-active' : 'badge-inactive'}`}>{label}</span>;
      },
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessorKey: 'createdAt',
      size: 130,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return val ? new Date(val).toLocaleDateString() : '-';
      },
    },
    {
      id: 'updatedAt',
      header: 'Updated',
      accessorKey: 'updatedAt',
      size: 130,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return val ? new Date(val).toLocaleDateString() : '-';
      },
    },
  ], []);

  // ---- Row actions ----
  const getRowActions = useCallback((app: AppGridRow): ActionMenuItem[] => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(app) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(app) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(app), variant: 'danger' },
  ], [handleView, handleEdit, handleDeleteClick]);

  // ---- View mode options ----
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  // ---- Action buttons ----
  const actionButtons: ActionButton[] = [
    { id: 'search', icon: <IconSystem.search size={18} />, label: 'Search Applications', onClick: () => setSearchPopupOpen(true), tooltip: 'Search Applications (F2)', variant: 'icon', visible: true },
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add Application', onClick: handleAddNew, tooltip: 'Register New Application', variant: 'icon', visible: true },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: refetch, tooltip: 'Refresh', variant: 'icon' },
    { id: 'export', icon: <IconSystem.download size={18} />, label: 'Export', onClick: () => notificationService.toast.info('Export feature coming soon.'), tooltip: 'Export', variant: 'icon' },
  ];

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Applications"
        description="Manage platform applications"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search applications...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid<AppGridRow>
              data={filteredData}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              rowActions={getRowActions}
              onSelectionChange={setSelectedRows}
              emptyMessage={loading ? 'Loading applications...' : 'No applications found'}
              visibleColumns={visibleColumns}
            />
          )}

          {viewMode === 'grid' && (() => {
            const totalPages = Math.max(1, Math.ceil(filteredData.length / gridPageSize));
            const safeGridPage = Math.min(gridPage, totalPages - 1);
            const pagedData = filteredData.slice(safeGridPage * gridPageSize, (safeGridPage + 1) * gridPageSize);
            return (
              <div className="syut-grid-component" style={{ height: '100%', overflow: 'auto' }}>
                <div style={{
                  padding: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1.5rem',
                }}>
                  {filteredData.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.grid size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>{loading ? 'Loading applications...' : 'No applications found'}</p>
                    </div>
                  ) : (
                    pagedData.map(app => (
                      <AppCard
                        key={app.appId}
                        app={app}
                        onView={handleView}
                      />
                    ))
                  )}
                </div>
                {filteredData.length > 0 && (
                  <div className="pagination-footer" style={{ position: 'sticky', bottom: 0 }}>
                    <div className="pagination-info">
                      Showing {pagedData.length} of {filteredData.length} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={gridPageSize}
                        onChange={(e) => { setGridPageSize(Number(e.target.value)); setGridPage(0); }}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => setGridPage(0)} disabled={safeGridPage === 0}>
                        <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => setGridPage(safeGridPage - 1)} disabled={safeGridPage === 0}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.75rem', fontSize: '0.85rem' }}>
                        Page {safeGridPage + 1} of {totalPages}
                      </span>
                      <button className="page-btn" onClick={() => setGridPage(safeGridPage + 1)} disabled={safeGridPage >= totalPages - 1}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => setGridPage(totalPages - 1)} disabled={safeGridPage >= totalPages - 1}>
                        <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {viewMode === 'calendar' && (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <IconSystem.calendar size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Calendar View Not Applicable</h3>
              <p>Calendar view is not available for Applications</p>
            </div>
          )}
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button
                onClick={() => { setCodeFilter(''); setNameFilter(''); setVersionFilter(''); setStatusFilter(''); setCreatedFromFilter(''); setCreatedToFilter(''); setFilterOpen(false); }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-muted)',
                  borderRadius: '0.25rem',
                }}
                title="Close filters"
              >
                <IconSystem.x size={16} />
              </button>
            </div>

            <div className="search-panel-content">
              {/* Code Filter */}
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={codeFilter}
                  onChange={(e) => setCodeFilter(e.target.value)}
                  placeholder="Filter by code..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              {/* Name Filter */}
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Application Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Filter by name..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              {/* Version Filter */}
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Version</label>
                <input
                  type="text"
                  className="form-input"
                  value={versionFilter}
                  onChange={(e) => setVersionFilter(e.target.value)}
                  placeholder="Filter by version..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              {/* Status Filter */}
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={[
                    { value: '', label: 'All Statuses' },
                    { value: 'app_active', label: 'Active' },
                    { value: 'app_inactive', label: 'Inactive' },
                  ]}
                  value={statusFilter}
                  onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                  placeholder="Select status..."
                  isClearable={false}
                  size="sm"
                />
              </div>

              {/* Created From Filter */}
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Created From</label>
                <input
                  type="date"
                  className="form-input"
                  value={createdFromFilter}
                  onChange={(e) => setCreatedFromFilter(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              {/* Created To Filter */}
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Created To</label>
                <input
                  type="date"
                  className="form-input"
                  value={createdToFilter}
                  onChange={(e) => setCreatedToFilter(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setCodeFilter(''); setNameFilter(''); setVersionFilter(''); setStatusFilter(''); setCreatedFromFilter(''); setCreatedToFilter(''); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <ApplicationFormModal
        isOpen={formModalOpen}
        mode={formMode}
        application={formMode === 'edit' ? selectedApp : null}
        onClose={() => { setFormModalOpen(false); setSelectedApp(null); }}
        onMinimize={() => setFormModalOpen(false)}
        onRestoreModal={(restoredMode, instanceId?, appCode?) => {
          if (instanceId) setCreateInstanceId(instanceId);
          if (appCode) {
            const a = applications.find(r => r.appCode === appCode);
            if (a) setSelectedApp(a);
          }
          setFormMode(restoredMode);
          setFormModalOpen(true);
        }}
        onSaveSuccess={handleFormSaveSuccess}
        tabId={tabId}
        moduleName={moduleName}
        createInstanceId={createInstanceId}
      />

      {/* View Modal */}
      <ApplicationViewModal
        isOpen={viewModalOpen}
        application={selectedApp}
        onClose={() => { setViewModalOpen(false); setSelectedApp(null); }}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Application"
        message={`Are you sure you want to delete "${selectedApp?.appName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteModalOpen(false); setSelectedApp(null); }}
      />

      {/* Application Search Popup (F2) */}
      <SyutSearchGrid<AppGridRow>
        isOpen={searchPopupOpen}
        onClose={() => setSearchPopupOpen(false)}
        onSelect={(app) => {
          handleView(app);
        }}
        title="Search Applications"
        columns={[
          { key: 'appCode', header: 'Code', width: '120px', sortable: true },
          { key: 'appName', header: 'Name', width: '200px', sortable: true },
          { key: 'appVersion', header: 'Version', width: '100px', sortable: true },
          {
            key: 'statusCode',
            header: 'Status',
            width: '100px',
            render: (value: any, row: any) => {
              const code = ((value as string) || '').toLowerCase();
              const isActive = code.includes('active');
              const label = isActive ? 'Active' : ((row?.statusName as string) || '').replace(/\s*application\s*/i, '') || '-';
              return (
                <span className={`badge ${isActive ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: '0.75rem' }}>
                  {label}
                </span>
              );
            }
          },
        ]}
        data={gridData}
        filters={[
          {
            key: 'statusCode',
            label: 'Status',
            type: 'select',
            options: [
              { value: 'APP_ACTIVE', label: 'Active' },
              { value: 'APP_INACTIVE', label: 'Inactive' },
            ]
          },
        ]}
        rowKey="id"
        enableRecentlyUsed
        recentlyUsedKey="application-master-search"
        searchPlaceholder="Search by code or name... (F2 to focus)"
      />
    </div>
  );
};

export default ApplicationMasterScreen;
